import {
  useDefaultHeaderContent,
  getPendingJobScopeChangeElements,
  getRatingsElements,
  PageHeader,
} from '@/features/jobs/details/components/PageHeader'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { observer } from 'mobx-react-lite'
import { type ReactNode } from 'react'
import {
  ExpectationsDialog,
  useExpectationsDialog,
} from '@/features/jobs/details/components/ExpectationsDialog'
import { Step, StepList } from '@/components/StepList'
import { match } from '@/utils/pattern-match-util'
import {
  ReviewDraftActionCardContainer,
  ReviewDraftDialogContainer,
  ReviewDraftScrolledActionContainer,
} from '@/features/jobs/details/tax-type/components/DraftReview'
import {
  ReadyToFileActionCardContainer,
  ReadyToFileConfirmationDialogContainer,
  ReadyToFileScrolledActionContainer,
} from '../ReadyToFile'
import { TaxInfoGatheringDialogsContainer } from '@/features/jobs/details/tax-type/info-gathering/components/TaxInfoGatheringDialogsContainer'
import {
  OpenItemsActionCardContainer,
  OpenItemsDialogContainer,
  OpenItemsScrolledActionContainer,
} from '@/features/jobs/details/tax-type/components/OpenItems/OpenItemsContainers'
import {
  RateJobActionCard,
  RateJobScrolledAction,
} from '@/features/jobs/details/ratings/components/RatingsJobDetailsHeader'
import {
  FinalReturnDownloadActionCardContainer,
  FinalReturnDownloadScrolledActionContainer,
} from '@/features/jobs/details/tax-type/components/FinalReturn/FinalReturnContainers'

export interface TaxJobDetailsHeaderProps {
  taxVm: TaxJobDetailsViewModel
  jobVm: JobDetailsViewModel
}

/**
 * Job details header for Tax jobs specifically.
 */
export const TaxJobDetailsHeader = observer(function TaxJobDetailsHeader({
  taxVm,
  jobVm,
}: TaxJobDetailsHeaderProps) {
  const defaultContent = useDefaultHeaderContent(jobVm)
  const hasCustomContent =
    jobVm.job.status !== 'Closed' && taxVm.progressionVm.currentStepContent
  const { title, description } = hasCustomContent
    ? {
        title: taxVm.progressionVm.currentStepContent.title,
        description: taxVm.progressionVm.currentStepContent.description,
      }
    : defaultContent
  const { scrolledAction, actionCard } = getActionElements(jobVm, taxVm)

  const { isOpen: isExpectationsDialogOpen, close: closeExpectationsDialog } =
    useExpectationsDialog()

  return (
    <>
      <PageHeader
        isSkeleton={!taxVm.progressionVm.fetchProgression.resolved}
        vm={jobVm}
        headline={title}
        description={description}
        scrolledAction={scrolledAction}
        scrolledHeadline={title}
        actionCard={actionCard}
      />
      <TaxJobExpectationsDialog
        isOpen={isExpectationsDialogOpen}
        onClose={closeExpectationsDialog}
        vm={taxVm}
      />
      <OpenItemsDialogContainer vm={taxVm} />
      <ReviewDraftDialogContainer vm={taxVm} />
      <ReadyToFileConfirmationDialogContainer vm={taxVm} />
      <TaxInfoGatheringDialogsContainer vm={taxVm} />
    </>
  )
})

interface TaxJobExpectationsDialogProps {
  vm: TaxJobDetailsViewModel
  isOpen: boolean
  onClose: () => unknown
}

/**
 * Tax Job Expectations Dialog
 */
export const TaxJobExpectationsDialog = observer(
  function TaxJobExpectationsDialog({
    vm,
    isOpen,
    onClose,
  }: TaxJobExpectationsDialogProps) {
    const isProgressionEmpty = vm.progressionVm.allSteps.length === 0

    return (
      <ExpectationsDialog
        isOpen={!isProgressionEmpty && isOpen}
        onClose={onClose}
        testId="tax-job-expectations-dialog"
        confirmTestId="tax-job-expectations-dialog-close-button"
      >
        <StepList>
          {vm.progressionVm.allSteps.map((step, index) => (
            <Step
              key={index}
              step={index + 1}
              testId={`tax-job-expectations-dialog-step-${step.step}`}
              title={step.content.title}
              state={match(step.state, {
                Current: 'current',
                Upcoming: 'pending',
                Completed: 'completed',
              })}
            >
              {step.content.description}
            </Step>
          ))}
        </StepList>
      </ExpectationsDialog>
    )
  },
)

function getActionElements(
  jobVm: JobDetailsViewModel,
  taxVm: TaxJobDetailsViewModel,
): {
  scrolledAction?: ReactNode
  actionCard?: ReactNode
} {
  const pendingJobScopeChangeElements = getPendingJobScopeChangeElements(jobVm)

  if (pendingJobScopeChangeElements) {
    return pendingJobScopeChangeElements
  }

  if (jobVm.job.status !== 'Closed') {
    const current = taxVm.progressionVm.currentStep
    switch (current) {
      case 'NotStarted':
      case 'OpenItems': {
        // If there are any incomplete checklist items, show a card for completing
        // the checklist.
        if (taxVm.infoGatheringVm.isChecklistIncomplete) {
          return {
            actionCard: <OpenItemsActionCardContainer vm={taxVm} />,
            scrolledAction: <OpenItemsScrolledActionContainer vm={taxVm} />,
          }
        }
        return {}
      }

      case 'DraftInReview':
        return {
          actionCard: <ReviewDraftActionCardContainer vm={taxVm} />,
          scrolledAction: <ReviewDraftScrolledActionContainer vm={taxVm} />,
        }

      case 'DraftApproved': {
        if (jobVm.ratingsVm.isRateable) {
          return {
            actionCard: (
              <RateJobActionCard
                kicker="Your draft return"
                title="Approved"
                onRatePress={jobVm.ratingsVm.showRatingModal}
                testId="job-details-header-leave-review-button"
              />
            ),
            scrolledAction: (
              <RateJobScrolledAction
                onRatePress={jobVm.ratingsVm.showRatingModal}
                testId="job-details-header-leave-review-button"
              />
            ),
          }
        }
        break
      }

      case 'ProgressionWaitingOnClient':
        return {
          actionCard: <ReadyToFileActionCardContainer vm={taxVm} />,
          scrolledAction: <ReadyToFileScrolledActionContainer vm={taxVm} />,
        }

      case 'ReturnFiled': {
        if (jobVm.ratingsVm.isRateable) {
          return {
            actionCard: (
              <RateJobActionCard
                kicker="Your tax return"
                title="Filed"
                onRatePress={jobVm.ratingsVm.showRatingModal}
                testId="job-details-header-leave-review-button"
              />
            ),
            scrolledAction: (
              <RateJobScrolledAction
                onRatePress={jobVm.ratingsVm.showRatingModal}
                testId="job-details-header-leave-review-button"
              />
            ),
          }
        }

        return {
          actionCard: <FinalReturnDownloadActionCardContainer vm={taxVm} />,
          scrolledAction: (
            <FinalReturnDownloadScrolledActionContainer vm={taxVm} />
          ),
        }
      }
    }
  }

  const ratingsElements = getRatingsElements(jobVm)

  if (ratingsElements) {
    return ratingsElements
  }

  return {}
}
